import React, { useEffect, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import StepHeading, { SectionCard, InfoNotice } from '../StepHeading';
import StepFooter from '../StepFooter';
import { useStepForm } from '../useStepForm';
import { TextInput, SelectInput, MonthInput, DateInput, YesNoToggle, CheckboxCard, CheckboxInline, RepeatableGroup, ErrorSummary } from '../../ui/form';
import FileSlot from '../../ui/FileSlot';
import { US_STATES } from '../../../utils/usStates';
import { LICENSE_CLASS_OPTIONS, ENDORSEMENT_OPTIONS, EQUIPMENT_OPTIONS, DOCUMENT_SLOTS } from '../stepConfig';
import { todayIso, isValidMonth } from '../../../utils/dateInput';
import { validateMonthRange } from '../../../utils/applicationCoverage';

const emptyLicense = (personal = {}) => ({
  firstNameOnLicense: personal.firstName || '',
  middleNameOnLicense: personal.middleName || '',
  lastNameOnLicense: personal.lastName || '',
  licenseNumber: '',
  state: personal.state || '',
  licenseClass: 'A',
  issued: '',
  expires: '',
  endorsements: [],
  isCurrent: false
});

const toFormValues = (application) => {
  const personal = application.sections?.personal || {};
  const licenses = application.sections?.licenses || {};
  const items = (licenses.items || []).map((item) => ({ ...emptyLicense(personal), ...item, endorsements: item.endorsements || [] }));
  if (!items.length) items.push({ ...emptyLicense(personal), isCurrent: true });
  const experience = application.sections?.experience || {};
  return {
    licenses: {
      items,
      singleLicenseCertification: Boolean(licenses.singleLicenseCertification),
      medicalCardExpiration: licenses.medicalCardExpiration || ''
    },
    experience: {
      hasDrivenCmvBefore: typeof experience.hasDrivenCmvBefore === 'boolean' ? experience.hasDrivenCmvBefore : null,
      equipment: (experience.equipment || []).map((e) => ({ type: e.type, from: e.from || '', to: e.to || '', isPresent: Boolean(e.isPresent), approxMiles: e.approxMiles ?? '' })),
      otherDescription: experience.otherDescription || ''
    }
  };
};

const toPayload = (values) => ({
  licenses: {
    items: (values.licenses?.items || []).map((item) => ({
      ...item,
      licenseNumber: (item.licenseNumber || '').toUpperCase().trim(),
      endorsements: Array.isArray(item.endorsements) ? item.endorsements : (item.endorsements ? [item.endorsements] : []),
      isCurrent: Boolean(item.isCurrent)
    })),
    singleLicenseCertification: Boolean(values.licenses?.singleLicenseCertification),
    medicalCardExpiration: values.licenses?.medicalCardExpiration || ''
  },
  experience: {
    hasDrivenCmvBefore: values.experience?.hasDrivenCmvBefore,
    equipment: (values.experience?.equipment || []).map((e) => ({
      type: e.type,
      from: e.from || '',
      to: e.isPresent ? null : (e.to || ''),
      isPresent: Boolean(e.isPresent),
      approxMiles: e.approxMiles === '' || e.approxMiles === null || e.approxMiles === undefined ? null : Number(e.approxMiles)
    })),
    otherDescription: values.experience?.otherDescription || ''
  }
});

const LABELS = {
  'licenses.items': 'Licenses', 'licenses.singleLicenseCertification': 'Single license certification', 'licenses.medicalCardExpiration': 'Medical card expiration',
  'experience.hasDrivenCmvBefore': 'Commercial driving experience', 'experience.equipment': 'Equipment operated', 'experience.otherDescription': 'Other equipment description',
  'licenses.items.*.firstNameOnLicense': 'License {n}: first name', 'licenses.items.*.lastNameOnLicense': 'License {n}: last name',
  'licenses.items.*.licenseNumber': 'License {n}: license number', 'licenses.items.*.state': 'License {n}: state', 'licenses.items.*.licenseClass': 'License {n}: class',
  'licenses.items.*.issued': 'License {n}: issue date', 'licenses.items.*.expires': 'License {n}: expiration date',
  'experience.equipment.*.from': 'Equipment {n}: from', 'experience.equipment.*.to': 'Equipment {n}: to', 'experience.equipment.*.approxMiles': 'Equipment {n}: miles',
  'documents.license_front': "Driver's license (front)", 'documents.license_back': "Driver's license (back)", 'documents.medical_card': 'Medical certificate', 'documents.twic_card': 'TWIC card'
};

const LicenseExperienceStep = ({ application, locked, flaggedMessage, onSaveDraft, onComplete, onContinue, onBack, onUpload, onRemoveDocument }) => {
  const defaultValues = useMemo(() => toFormValues(application), [application.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const personal = useMemo(() => application.sections?.personal || {}, [application.sections?.personal]);
  const documents = application.documents || [];
  const today = todayIso();

  const requiredSlots = useMemo(() => DOCUMENT_SLOTS.filter((s) => s.required || (s.conditional === 'hasTwicCard' && personal.hasTwicCard)), [personal]);
  const visibleSlots = useMemo(() => DOCUMENT_SLOTS.filter((s) => !s.conditional || personal[s.conditional]), [personal]);

  const validate = (values) => {
    const problems = [];
    const items = values.licenses?.items || [];
    const currentCount = items.filter((i) => i.isCurrent).length;
    if (items.length && currentCount !== 1) {
      problems.push({ name: 'licenses.items', message: currentCount === 0 ? 'Tick "This is my current license" on one license' : 'Only one license can be your current license' });
    }
    items.forEach((item, index) => {
      if (item.issued && item.expires && item.expires <= item.issued) problems.push({ name: `licenses.items.${index}.expires`, message: 'Expiration must be after the issue date' });
      if (item.isCurrent && item.expires && item.expires < today) problems.push({ name: `licenses.items.${index}.expires`, message: 'Your current license appears to be expired' });
    });
    if (values.licenses?.medicalCardExpiration && values.licenses.medicalCardExpiration < today) {
      problems.push({ name: 'licenses.medicalCardExpiration', message: 'Your medical card appears to be expired. A current certificate is required.' });
    }
    (values.experience?.equipment || []).forEach((e, index) => {
      const problem = validateMonthRange({ from: e.from, to: e.to, isPresent: e.isPresent });
      if (problem) problems.push({ name: `experience.equipment.${index}.from`, message: problem });
    });
    if (values.experience?.hasDrivenCmvBefore && !(values.experience.equipment || []).length) {
      problems.push({ name: 'experience.equipment', message: 'Select the equipment you have operated' });
    }
    if ((values.experience?.equipment || []).some((e) => e.type === 'other') && !values.experience?.otherDescription) {
      problems.push({ name: 'experience.otherDescription', message: 'Describe the other equipment' });
    }
    const uploaded = new Set(documents.map((d) => d.slot));
    requiredSlots.forEach((slot) => {
      if (!uploaded.has(slot.slot)) problems.push({ name: `documents.${slot.slot}`, message: `Upload your ${slot.label.toLowerCase()}` });
    });
    return problems;
  };

  const { form, values, errors, autosave, submitting, serverMessage, failedAttempts, submitNext } = useStepForm({
    stepId: 'license', defaultValues, toPayload, onSaveDraft, onComplete, locked, validate
  });
  const { register, control, setValue, getValues, clearErrors } = form;

  // Drop "Upload your …" errors (and their error-summary entries) once the slot has a file.
  const uploadedSlots = documents.map((d) => d.slot).join(',');
  useEffect(() => {
    documents.forEach((d) => { if (errors.documents?.[d.slot]) clearErrors(`documents.${d.slot}`); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedSlots]);
  const licenseArray = useFieldArray({ control, name: 'licenses.items' });
  const equipmentArray = useFieldArray({ control, name: 'experience.equipment' });

  // Keep "current" exclusive: ticking one unticks the others. Done in the
  // change handler because only there do we know which box the user just ticked.
  const registerCurrent = (index) => {
    const field = register(`licenses.items.${index}.isCurrent`);
    return {
      ...field,
      onChange: (event) => {
        if (event.target.checked) {
          (getValues('licenses.items') || []).forEach((item, other) => {
            if (other !== index && item?.isCurrent) setValue(`licenses.items.${other}.isCurrent`, false, { shouldDirty: true });
          });
        }
        return field.onChange(event);
      }
    };
  };

  const equipmentIndex = (type) => equipmentArray.fields.findIndex((f) => f.type === type);
  const toggleEquipment = (type) => {
    const index = equipmentIndex(type);
    if (index >= 0) equipmentArray.remove(index);
    else equipmentArray.append({ type, from: '', to: '', isPresent: false, approxMiles: '' });
  };

  const docsForSlot = (slot) => documents.filter((d) => d.slot === slot);
  // "Upload your …" errors go stale as soon as that slot has a file.
  const docErrors = Object.fromEntries(Object.entries(errors.documents || {})
    .filter(([slot]) => !documents.some((d) => d.slot === slot))
    .map(([k, v]) => [k, v?.message]));

  return (
    <form onSubmit={(e) => { e.preventDefault(); submitNext(); }} noValidate>
      <StepHeading title="License & Experience" description="List every driver's license you have held in the last 3 years, upload your documents, and tell us what you have driven." flaggedMessage={flaggedMessage} locked={locked} />
      <ErrorSummary errors={errors} labels={LABELS} focusKey={failedAttempts} className="mb-5" />
      {serverMessage && !Object.keys(errors).length && <p className="mb-4 text-sm text-red-600" role="alert">{serverMessage}</p>}

      <fieldset disabled={locked} className="space-y-5">
        <SectionCard title="Driver's licenses" description="Enter your name exactly as it appears on each license. 49 CFR 383.21: no person who operates a commercial motor vehicle may hold more than one driver's license.">
          {errors.licenses?.items?.message && <p className="mb-3 text-sm text-red-600" role="alert">{errors.licenses.items.message}</p>}
          <RepeatableGroup
            items={licenseArray.fields}
            title={(i, item) => (values.licenses?.items?.[i]?.isCurrent ? `License ${i + 1} (current)` : `License ${i + 1}`)}
            addLabel="Add another license held in the last 3 years"
            onAdd={() => licenseArray.append(emptyLicense(personal))}
            onRemove={licenseArray.remove}
            minItems={1}
            maxItems={6}
            disabled={locked}
            renderItem={(index) => {
              const e = errors.licenses?.items?.[index] || {};
              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <TextInput label="First name on license" required {...register(`licenses.items.${index}.firstNameOnLicense`, { required: 'Required' })} error={e.firstNameOnLicense?.message} />
                    <TextInput label="Middle name" optionalLabel {...register(`licenses.items.${index}.middleNameOnLicense`)} />
                    <TextInput label="Last name on license" required {...register(`licenses.items.${index}.lastNameOnLicense`, { required: 'Required' })} error={e.lastNameOnLicense?.message} />
                    <TextInput label="License number" required autoCapitalize="characters" inputClassName="uppercase" {...register(`licenses.items.${index}.licenseNumber`, { required: 'License number is required', pattern: { value: /^[A-Za-z0-9-]{1,20}$/, message: 'Letters, numbers and dashes only' } })} error={e.licenseNumber?.message} />
                    <SelectInput label="State" required options={US_STATES} {...register(`licenses.items.${index}.state`, { required: 'State is required' })} error={e.state?.message} />
                    <SelectInput label="License class" required placeholder={null} options={LICENSE_CLASS_OPTIONS} {...register(`licenses.items.${index}.licenseClass`, { required: 'Required' })} error={e.licenseClass?.message} />
                    <DateInput label="Issued" required max={today} {...register(`licenses.items.${index}.issued`, { required: 'Issue date is required' })} error={e.issued?.message} />
                    <DateInput label="Expires" required {...register(`licenses.items.${index}.expires`, { required: 'Expiration date is required' })} error={e.expires?.message} />
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-medium text-gray-700">Endorsements</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {ENDORSEMENT_OPTIONS.map((opt) => (
                        <CheckboxCard key={opt.value} label={opt.label} value={opt.value} {...register(`licenses.items.${index}.endorsements`)} />
                      ))}
                    </div>
                  </div>
                  <CheckboxInline label="This is my current license" {...registerCurrent(index)} />
                </div>
              );
            }}
          />
          <div className="mt-5 space-y-4">
            <CheckboxInline
              {...register('licenses.singleLicenseCertification', { required: 'You must certify that you hold only one driver\'s license' })}
              error={errors.licenses?.singleLicenseCertification?.message}
              label={<span>I certify that I do not have more than one motor vehicle license, and that every license I have held in the past 3 years is listed above. <span className="text-red-600">*</span></span>}
            />
          </div>
        </SectionCard>

        <SectionCard title="Your documents" description="Take a photo or upload a file. JPG, PNG or PDF, up to 10 MB each.">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {visibleSlots.map((slot) => {
              const existing = docsForSlot(slot.slot);
              if (slot.multiple) {
                return (
                  <div key={slot.slot} className="md:col-span-2 space-y-3">
                    {existing.map((doc) => (
                      <FileSlot key={doc.id} label={slot.label} hint={slot.hint} existing={doc} onUpload={(file, onProgress) => onUpload(slot.slot, file, onProgress)} onRemove={() => onRemoveDocument(doc.id)} disabled={locked} />
                    ))}
                    <FileSlot label={existing.length ? 'Add another document' : slot.label} hint={slot.hint} onUpload={(file, onProgress) => onUpload(slot.slot, file, onProgress)} disabled={locked} />
                  </div>
                );
              }
              return (
                <FileSlot
                  key={slot.slot}
                  label={slot.label}
                  hint={slot.hint}
                  required={requiredSlots.some((r) => r.slot === slot.slot)}
                  existing={existing[0] || null}
                  onUpload={(file, onProgress) => onUpload(slot.slot, file, onProgress)}
                  onRemove={existing[0] ? () => onRemoveDocument(existing[0].id) : undefined}
                  disabled={locked}
                  error={docErrors[slot.slot]}
                  fieldName={`documents.${slot.slot}`}
                />
              );
            })}
            <DateInput label="Medical card expiration date" required hint="From your medical examiner's certificate" {...register('licenses.medicalCardExpiration', { required: 'Medical card expiration is required' })} error={errors.licenses?.medicalCardExpiration?.message} />
          </div>
        </SectionCard>

        <SectionCard title="Experience" description="What type of equipment have you operated?">
          <YesNoToggle name="experience.hasDrivenCmvBefore" control={control} label="Have you driven a commercial motor vehicle before?" required hint="If yes, we will ask for 10 years of employment history on the next steps (49 CFR 391.21)." />
          <div className="mt-4">
            <p className="mb-2 text-sm font-medium text-gray-700">Equipment operated {errors.experience?.equipment?.message && <span className="ml-2 font-normal text-red-600">{errors.experience.equipment.message}</span>}</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {EQUIPMENT_OPTIONS.map((opt) => (
                <CheckboxCard key={opt.value} label={opt.label} checked={equipmentIndex(opt.value) >= 0} onChange={() => toggleEquipment(opt.value)} name={`equipment-${opt.value}`} />
              ))}
            </div>
          </div>
          {equipmentArray.fields.length > 0 && (
            <div className="mt-4 space-y-3">
              {equipmentArray.fields.map((field, index) => {
                const opt = EQUIPMENT_OPTIONS.find((o) => o.value === field.type);
                const e = errors.experience?.equipment?.[index] || {};
                const isPresent = Boolean(values.experience?.equipment?.[index]?.isPresent);
                return (
                  <div key={field.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="mb-2 text-sm font-semibold text-gray-900">{opt?.label || field.type}</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <MonthInput label="From" required {...register(`experience.equipment.${index}.from`, { required: 'Required', validate: (v) => isValidMonth(v) || 'Choose a month and year' })} error={e.from?.message} />
                      <MonthInput label="To" disabled={isPresent} {...register(`experience.equipment.${index}.to`, { validate: (v) => isPresent || !v || isValidMonth(v) || 'Choose a month and year' })} error={e.to?.message} />
                      <div className="flex items-end pb-2">
                        <CheckboxInline label="Present" {...register(`experience.equipment.${index}.isPresent`)} />
                      </div>
                      <TextInput label="Approx. miles" optionalLabel type="number" inputMode="numeric" min={0} {...register(`experience.equipment.${index}.approxMiles`)} error={e.approxMiles?.message} />
                    </div>
                    {field.type === 'other' && (
                      <TextInput className="mt-3" label="Describe the other equipment" required {...register('experience.otherDescription', { required: 'Describe the other equipment' })} error={errors.experience?.otherDescription?.message} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {values.experience?.hasDrivenCmvBefore === false && (
            <InfoNotice>No commercial experience is fine. You can leave the equipment list empty.</InfoNotice>
          )}
        </SectionCard>
      </fieldset>

      <StepFooter onBack={onBack} onNext={locked ? onContinue : submitNext} isSubmitting={submitting} saveStatus={autosave.status} lastSavedAt={autosave.lastSavedAt} onRetrySave={autosave.saveNow} nextDisabled={locked && !onContinue} nextLabel={locked ? 'Continue' : 'Save & continue'} />
    </form>
  );
};

export default LicenseExperienceStep;
